import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    AFL_SEASON_SHORT_LABEL,
    BookmakerLogo,
    BOOKMAKER_DISPLAY_NAMES,
    computeMultiMetrics,
    formatOdds,
    formatPct,
    formatStartTime,
    shortenMatchName,
} from './multiBuilderHelpers';

/**
 * Build the comma-separated `bet` string sent to the tracker. We keep each leg
 * compact — "Player - Market" — with a milestone shortcut so markets like
 * "To Record 2+ Goals" condense to "Player 2+ goals" (mirrors the SnipeFeed
 * `formatBetDescription` fallback path).
 */
function formatLegDescription(leg) {
    const player = leg.player || 'Unknown';
    const market = leg.market || '';
    const milestoneMatch = market.match(/To Record (\d+\+)\s+(\w+)/i);
    if (milestoneMatch) {
        return `${player} ${milestoneMatch[1]} ${milestoneMatch[2].toLowerCase()}`;
    }
    return player !== 'Unknown' && market ? `${player} - ${market}` : market || 'Unknown leg';
}

function AddToTrackerMultiModal({
    legs,
    multiBookmaker,
    bookmakerLogos,
    aflSeasonView,
    hasSameGameLegs,
    unitSize = 20,
    sport = 'AFL',
    onClose,
    onAddToTracker,
    isAddingBet,
}) {
    const [stake, setStake] = useState('');
    const stakeInputRef = useRef(null);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        const t = setTimeout(() => stakeInputRef.current?.focus(), 50);
        return () => {
            document.body.style.overflow = '';
            clearTimeout(t);
        };
    }, []);

    // Single source of truth for combined odds + EV%, computed from the legs
    // we render below. Identical to the panel's calculation so the modal can't
    // disagree with what the user just saw.
    const metrics = useMemo(() => computeMultiMetrics(legs || []), [legs]);
    const seasonShort = AFL_SEASON_SHORT_LABEL[aflSeasonView] || aflSeasonView || '';

    const combinedOdds = metrics.combinedOdds;
    const stakeNum = parseFloat(stake);
    const hasValidStake = stake && !isNaN(stakeNum) && stakeNum > 0;
    const hasPayout = hasValidStake && combinedOdds != null;
    const payout = hasPayout ? (stakeNum * combinedOdds).toFixed(2) : null;

    const renderBookmakerLogo = (bookmaker, size = 24) => {
        const logo = bookmakerLogos?.[bookmaker];
        const name = BOOKMAKER_DISPLAY_NAMES[bookmaker] || bookmaker;
        if (logo) {
            return (
                <img
                    src={logo}
                    alt={name}
                    style={{ width: size, height: size }}
                    className="rounded-sm object-cover"
                    title={name}
                />
            );
        }
        return (
            <span className="text-[9px] font-mono text-slate-500 uppercase tracking-wider" title={name}>
                {name?.substring(0, 3)}
            </span>
        );
    };

    const legCount = legs?.length || 0;
    const canSubmit = legCount >= 2 && hasPayout && !!multiBookmaker && combinedOdds != null;

    const handleSubmit = async () => {
        if (!onAddToTracker || !canSubmit) return;

        const earliest = legs.reduce((min, leg) => {
            if (!leg?.startTime) return min;
            const t = new Date(leg.startTime).getTime();
            if (!Number.isFinite(t)) return min;
            return min == null || t < min ? t : min;
        }, null);
        const d = earliest != null ? new Date(earliest) : new Date();
        const betDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

        const bookieName = BOOKMAKER_DISPLAY_NAMES[multiBookmaker] || multiBookmaker;
        const betDesc = legs.map(formatLegDescription).join(', ');
        const roundedOdds = Number(combinedOdds.toFixed(4));
        const computedPayout = Number((stakeNum * roundedOdds).toFixed(2));

        await onAddToTracker({
            date: betDate,
            sport: (sport || 'AFL').toUpperCase(),
            game: 'MULTI',
            bet: betDesc,
            odds: roundedOdds,
            stake: stakeNum,
            payout: computedPayout,
            bookie: bookieName,
            result: 'pending',
            pl: 0,
            bonus_bet: false,
        });
    };

    if (!legs || legCount === 0) return null;

    return createPortal(
        <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9999] p-0 sm:p-4"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="relative bg-slate-900 border border-slate-700/50 rounded-xl w-full max-w-md mx-0 sm:mx-4 shadow-2xl overflow-hidden animate-slide-up max-h-[95vh] sm:max-h-[90vh] flex flex-col">
                {/* Header bar */}
                <div className="px-4 py-3 border-b border-slate-700/50 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-2 min-w-0">
                        <svg className="w-4 h-4 text-emerald-400 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6.429 9.75 2.25 12l4.179 2.25m0-4.5 5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L21.75 12l-4.179 2.25m0 0 4.179 2.25L12 21.75 2.25 16.5l4.179-2.25m11.142 0-5.571 3-5.571-3" />
                        </svg>
                        <h3 className="text-sm font-semibold text-white truncate tracking-tight">
                            Track Multi
                        </h3>
                        <div className="w-px h-4 bg-slate-700/50 flex-shrink-0" />
                        <span className="text-[10px] font-mono text-slate-500 uppercase tracking-widest whitespace-nowrap">
                            {legCount} legs
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 rounded text-slate-500 hover:text-white hover:bg-slate-800 transition-all ml-3 flex-shrink-0"
                        aria-label="Close"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Ticket banner — combined odds + bookmaker */}
                <div className="relative px-4 pt-4 pb-3 border-b border-dashed border-slate-700/60 bg-gradient-to-br from-slate-900 to-slate-950 flex-shrink-0">
                    {/* Decorative perforations — matches the desktop panel banner */}
                    <span aria-hidden="true" className="absolute -left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-[#020817] border border-slate-700/40" />
                    <span aria-hidden="true" className="absolute -right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-[#020817] border border-slate-700/40" />

                    <div className="flex items-end justify-between gap-3">
                        <div className="min-w-0">
                            <div className="text-[10px] font-mono text-emerald-400 uppercase tracking-[0.18em] leading-none">
                                {hasSameGameLegs ? 'Non-correlated odds' : 'Combined odds'}
                            </div>
                            <div className="flex items-baseline gap-2 mt-2">
                                <span className="text-3xl font-mono font-bold tabular-nums text-white leading-none">
                                    {combinedOdds != null ? combinedOdds.toFixed(2) : '—'}
                                </span>
                                {Number.isFinite(metrics.marketEvPct) && (
                                    <span className={`text-xs font-mono font-semibold tabular-nums ${
                                        metrics.marketEvPct >= 0 ? 'text-emerald-400' : 'text-red-400'
                                    }`}>
                                        {metrics.marketEvPct >= 0 ? '+' : ''}{metrics.marketEvPct.toFixed(1)}%
                                    </span>
                                )}
                            </div>
                        </div>
                        {multiBookmaker && (
                            <div className="flex flex-col items-end flex-shrink-0">
                                <div className="text-[9px] font-mono text-slate-600 uppercase tracking-widest mb-1.5">
                                    Bookmaker
                                </div>
                                <div className="flex items-center gap-1.5">
                                    {renderBookmakerLogo(multiBookmaker, 18)}
                                    <span className="text-[12px] font-mono font-semibold text-slate-200 truncate max-w-[120px]">
                                        {BOOKMAKER_DISPLAY_NAMES[multiBookmaker] || multiBookmaker}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    {hasSameGameLegs && (
                        <div className="mt-3 flex items-start gap-2 px-2.5 py-2 rounded-md border border-amber-500/25 bg-amber-500/5">
                            <svg className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-400" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-4M12 8h.01" />
                            </svg>
                            <p className="text-[10.5px] font-mono text-amber-200/90 leading-snug">
                                Same-game legs are recorded at the independent-event price. The bookmaker&apos;s actual SGM payout may differ.
                            </p>
                        </div>
                    )}
                </div>

                {/* Scrollable middle */}
                <div className="flex-1 min-h-0 overflow-y-auto styled-scrollbar">
                    {/* Legs list */}
                    <div className="px-4 pt-3 pb-1">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] font-mono text-slate-600 uppercase tracking-widest">
                                Legs
                            </span>
                            <span className="text-[10px] font-mono text-slate-700 tabular-nums">
                                {legCount}
                            </span>
                        </div>
                        <div className="rounded-md border border-slate-800/70 divide-y divide-slate-800/70 overflow-hidden">
                            {legs.map((leg) => (
                                <ModalLegRow key={leg.key} leg={leg} />
                            ))}
                        </div>
                    </div>

                    {/* Metrics row */}
                    <div className="px-4 py-3">
                        <div className="grid grid-cols-2 gap-2">
                            <ModalMetric
                                label="Market EV%"
                                value={metrics.marketEvPct != null ? formatPct(metrics.marketEvPct, { digits: 2 }) : '—'}
                                accent={metrics.marketEvPct != null && metrics.marketEvPct >= 0 ? 'emerald' : metrics.marketEvPct != null ? 'red' : undefined}
                                sub={metrics.fairCombinedOdds != null ? `avg market ${formatOdds(metrics.fairCombinedOdds)}` : null}
                            />
                            <ModalMetric
                                label={`Implied EV% (${seasonShort})`}
                                value={metrics.historicalEvPct != null ? formatPct(metrics.historicalEvPct, { digits: 2 }) : '—'}
                                accent={metrics.historicalEvPct != null && metrics.historicalEvPct >= 0 ? 'emerald' : metrics.historicalEvPct != null ? 'red' : undefined}
                                sub={metrics.combinedImpliedOdds != null ? `implied ${formatOdds(metrics.combinedImpliedOdds)}` : 'no hit-rate data'}
                            />
                        </div>
                    </div>
                </div>

                {/* Stake input + payout preview */}
                <div className="px-4 py-3 border-t border-slate-700/30 flex-shrink-0">
                    {/* Variance disclaimer — sky-toned (kept distinct from the
                        amber SGM warning above) so users see it as a staking
                        heads-up, not an error. Appears immediately above the
                        stake input where it's most contextual. */}
                    <div className="mb-3 flex items-start gap-2 px-2.5 py-2 rounded-md border border-sky-500/25 bg-sky-500/5">
                        <svg className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-sky-400" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
                            <circle cx="12" cy="12" r="10" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-4M12 8h.01" />
                        </svg>
                        <p className="text-[10.5px] font-mono text-sky-200/90 leading-snug">
                            Multis are high variance, even when +EV. We recommend sizing your stake smaller than you would on a straight value bet.
                        </p>
                    </div>
                    <div className="text-[10px] font-mono text-slate-600 uppercase tracking-widest mb-2">Stake</div>
                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs font-mono">$</span>
                            <input
                                ref={stakeInputRef}
                                type="number"
                                step="0.01"
                                min="0"
                                value={stake}
                                onChange={(e) => setStake(e.target.value)}
                                placeholder="0.00"
                                className="w-full pl-6 pr-3 py-2 bg-slate-800 border border-slate-700 rounded text-sm font-mono text-white placeholder-slate-600 focus:outline-none focus:border-emerald-500 transition-colors"
                            />
                        </div>
                        <div className="flex gap-1.5">
                            {[
                                { label: '1U', mult: 1 },
                                { label: '0.5U', mult: 0.5 },
                                { label: '0.2U', mult: 0.2 },
                            ].map((unit) => {
                                const unitVal = (unitSize * unit.mult).toFixed(2);
                                return (
                                    <button
                                        key={unit.label}
                                        type="button"
                                        onClick={() => setStake(unitVal)}
                                        className={`px-3 py-2 rounded text-xs font-mono font-medium transition-all border ${
                                            stake === unitVal
                                                ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                                                : 'bg-slate-800 text-slate-400 border-slate-700 hover:text-white hover:border-slate-600'
                                        }`}
                                    >
                                        {unit.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {hasPayout && (
                        <div className="flex items-center justify-between mt-3 py-2.5 px-3 bg-emerald-500/5 border border-emerald-500/15 rounded">
                            <div>
                                <div className="text-[10px] font-mono text-slate-600 uppercase tracking-widest">Potential Payout</div>
                                <div className="text-sm font-mono font-bold text-emerald-400 tabular-nums">${payout}</div>
                            </div>
                            <div className="text-right">
                                <div className="text-[10px] font-mono text-slate-600 uppercase tracking-widest">Profit</div>
                                <div className="text-sm font-mono font-bold text-emerald-400 tabular-nums">
                                    ${(parseFloat(payout) - stakeNum).toFixed(2)}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer actions */}
                <div className="px-4 py-3 border-t border-slate-700/30 flex gap-2 flex-shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 px-4 py-2 text-xs font-mono font-medium text-slate-400 hover:text-white rounded border border-slate-700 hover:bg-slate-800 hover:border-slate-600 transition-all"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        disabled={isAddingBet || !canSubmit}
                        onClick={handleSubmit}
                        className="flex-1 px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded text-xs font-mono font-semibold hover:from-emerald-500 hover:to-teal-500 transition-all shadow-lg shadow-emerald-500/10 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                    >
                        {isAddingBet ? (
                            <span className="flex items-center justify-center gap-2">
                                <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                Adding...
                            </span>
                        ) : 'Add to Tracker'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

function ModalLegRow({ leg }) {
    return (
        <div className="relative pl-3 pr-3 py-2 bg-slate-900/40">
            <span aria-hidden="true" className="absolute left-0 top-2 bottom-2 w-[2px] rounded-r bg-emerald-400/40" />
            <div className="flex items-baseline justify-between gap-2">
                <div className="flex items-baseline gap-1.5 min-w-0 flex-1">
                    <span className="text-xs font-semibold text-slate-200 whitespace-nowrap flex-shrink-0" title={leg.player}>
                        {leg.player}
                    </span>
                    <span className="text-slate-700 flex-shrink-0">·</span>
                    <span className="text-[11px] font-mono text-slate-400 truncate min-w-0" title={leg.market}>
                        {leg.market}
                    </span>
                </div>
                <span className="text-[12px] font-mono font-bold tabular-nums whitespace-nowrap text-emerald-400">
                    {formatOdds(leg.offeredOdds)}
                </span>
            </div>
            <div className="text-[10px] font-mono text-slate-600 uppercase tracking-wider truncate mt-1">
                {shortenMatchName(leg.match, 'afl')}
                {leg.startTime && (
                    <>
                        <span className="text-slate-700"> · </span>
                        <span>{formatStartTime(leg.startTime)}</span>
                    </>
                )}
            </div>
        </div>
    );
}

function ModalMetric({ label, value, sub, accent }) {
    const color = accent === 'red'
        ? 'text-red-400'
        : accent === 'emerald'
            ? 'text-emerald-400'
            : 'text-white';
    return (
        <div className="min-w-0 px-2.5 py-2 bg-slate-900/60 border border-slate-700/30 rounded">
            <div className="text-[9px] font-mono text-slate-600 uppercase tracking-widest leading-none">
                {label}
            </div>
            <div className={`text-base font-mono font-bold tabular-nums mt-1 ${color}`}>
                {value}
            </div>
            {sub && (
                <div className="text-[9px] font-mono text-slate-600 tabular-nums mt-0.5 truncate">{sub}</div>
            )}
        </div>
    );
}

export default AddToTrackerMultiModal;
